import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import API from '@repositories/Axios';

/**
 * 프로바이더 소개 페이지 응답 타입
 */
export interface ProviderIntroDTO {
  id: string;
  providerId: string;
  content: string;
  status: 'DRAFT' | 'PUBLISHED';
  createdAt: string;
  updatedAt: string;
}

const providerIntroQueryKey = {
  all: ['providerIntro'] as const,
  detail: (providerId: string, lang: string) => [...providerIntroQueryKey.all, providerId, lang] as const,
};

/**
 * 프로바이더별 발행된 소개 페이지 조회
 * - 발행된 데이터가 있으면 해당 데이터 반환
 * - 없으면 null 반환 (404 응답 시)
 */
export const useGetProviderIntro = (
  providerId: string | undefined,
  lang: string,
  options?: Omit<UseQueryOptions<ProviderIntroDTO | null, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ProviderIntroDTO | null, AxiosError>({
    queryKey: providerIntroQueryKey.detail(providerId ?? '', lang),
    queryFn: async () => {
      try {
        const response = await API.get<ProviderIntroDTO>(
          `/api/v1/product-providers/${providerId}/intro`,
          { params: { lang } },
        );
        return response.data;
      } catch (error) {
        // 404인 경우 데이터가 없는 것으로 처리
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!providerId,
    ...options,
  });
};
